import {
    IBrandLibraryElement,
    ImageLibraryAsset,
    INewBrandLibraryElement,
    PartialLibraryAsset
} from '@domain/brand/brand-library';
import {
    IWidgetInstance,
    WidgetCode,
    WidgetResourceCache
} from '@domain/creative/elements/widget/widget-renderer.header';
import { IDesign, IElement, IElementProperty } from '@domain/creativeset';
import { AssetReference } from '@domain/creativeset/element-asset';
import { IWidgetText } from '@domain/creativeset/version';
import { IFontStyle } from '@domain/font';
import { ICreativeDataNode } from '@domain/nodes';
import {
    IWidgetCustomProperty,
    IWidgetElementProperty,
    IWidgetImage,
    OneOfCustomPropertyValue,
    WIDGET_PROPERTY_PREFIX
} from '@domain/widget';
import { getWidgetContentUrlOfElement, WidgetElement } from '@studio/utils/element.utils';
import { PromiseResolver } from '@studio/utils/promises';
import { resolveNamespacedCharacters } from '@studio/utils/utils';
import { isWidgetElement, isWidgetNode } from '../../nodes/helpers';
import { deserializeWidgetPropertyValue } from '../../serialization/widget-serializer';

export const codePropertyRegex = /^(html|css|ts|js)$/;
export const designWidgetElementCodePropertyRegex = /^(html|css|js)$/;

export function stripCustomPropertyPrefix(propertyName: string): string {
    return propertyName.replace(WIDGET_PROPERTY_PREFIX, '');
}

export function applyCustomPropertyPrefix(propertyName: string): string {
    if (propertyName.indexOf(WIDGET_PROPERTY_PREFIX) > -1) {
        return propertyName;
    }

    return `${WIDGET_PROPERTY_PREFIX}${propertyName}`;
}

export function isWidgetReference(property: IElementProperty): boolean {
    return property.name === AssetReference.Widget;
}

export function isWidgetContentBlobReference(property: IElementProperty): boolean {
    return property.name === AssetReference.WidgetContentUrl;
}

export function isBFLibraryWidgetReference(property: IElementProperty): boolean {
    return property.name === AssetReference.BannerflowLibraryWidget;
}

export function isAnyWidgetReference(property: IElementProperty): boolean {
    return (
        isWidgetReference(property) ||
        isWidgetContentBlobReference(property) ||
        isBFLibraryWidgetReference(property)
    );
}

export function isCustomProperty(property: IElementProperty): property is IWidgetElementProperty {
    return property.name.indexOf(WIDGET_PROPERTY_PREFIX) > -1;
}

export function isWidgetTextProperty(
    property: IWidgetCustomProperty
): property is IWidgetCustomProperty & { value: IWidgetText } {
    return property.unit === 'text';
}

export function isWidgetCodeProperty(property: Partial<IElementProperty>): boolean {
    return property.name !== undefined && property.name.match(codePropertyRegex) !== null;
}

export function getIframeIdFromEvent(mouseEvent: MouseEvent): string | undefined {
    const currentTarget = mouseEvent.currentTarget;
    if (!currentTarget) {
        return;
    }
    if ((currentTarget as Document).defaultView) {
        return (currentTarget as Document).defaultView?.frameElement?.id;
    }
    return (currentTarget as HTMLElement)?.ownerDocument?.defaultView?.frameElement?.id;
}

export function translateOpenEventPosition(
    mouseEvent: MouseEvent,
    widgetInstance: IWidgetInstance | undefined
): { x: number; y: number } {
    if (!widgetInstance?.instance) {
        return {
            x: mouseEvent.clientX,
            y: mouseEvent.clientY
        };
    }

    const { x: widgetX, y: widgetY } = widgetInstance.instance;
    // mouseEvent.clientX and clientY values are relative to the iframe
    return {
        x: mouseEvent.clientX + widgetX,
        y: mouseEvent.clientY + widgetY
    };
}

export function isValidWidgetResourceData(data: unknown): data is WidgetCode {
    return !!data && typeof data === 'object' && 'html' in data && 'css' in data && 'js' in data;
}

const widgetResourceCache = new Map<string, WidgetResourceCache>();

export async function fetchWidgetFromResource(resourceUrl: string): Promise<WidgetCode> {
    resourceUrl = resourceUrl.replace(
        'http://storage-emulator:10000',
        'https://blob-storage.bannerflow.local'
    );
    const existingResource = widgetResourceCache.get(resourceUrl);
    if (existingResource) {
        return await existingResource.promise;
    }

    const loadWidgetResource = new PromiseResolver<WidgetCode>();
    const promiseObj = { promise: loadWidgetResource.promise };

    let response: unknown;
    widgetResourceCache.set(resourceUrl, promiseObj);

    try {
        response = await (await fetch(resourceUrl)).json();
    } catch {
        loadWidgetResource.reject();
        throw new Error('Could not fetch widget resource.');
    }

    if (!isValidWidgetResourceData(response)) {
        throw new Error('Invalid widget resource data.');
    }

    widgetResourceCache.set(resourceUrl, promiseObj);

    loadWidgetResource.resolve(response);

    return response;
}

export const DEFAULT_WIDGET_JS = `Widget.on(WidgetEvent.PropertyChanged, () => {
    console.log(Widget.properties);
})`;
export const DEFAULT_WIDGET_HTML = `<div>Hello!</div>\n`;
export const DEFAULT_WIDGET_CSS = `html, body {
    padding: 0;
    margin: 0;
}`;

export async function getWidgetCodeOfElement(element: WidgetElement): Promise<WidgetCode> {
    const resourceUrl =
        '$type' in element ? element.widgetContentBlobReference : getWidgetContentUrlOfElement(element);

    if (!resourceUrl) {
        throw new Error('No widget content blob url found.');
    }

    return await fetchWidgetFromResource(resourceUrl);
}

// Temporarily used to apply widget code on widget nodes in manage view. remove after IDesign removal
export async function applyWidgetCodeOnSapiWidgetNodes(design: IDesign): Promise<void> {
    for (const element of design.elements) {
        if (isWidgetElement(element)) {
            const node = design.document.findNodeById_m(element.id);
            if (isWidgetNode(node)) {
                const { html, css, js } = await getWidgetCodeOfElement(element);
                node.html = html;
                node.css = css;
                node.js = js;
            }
        }
    }
}

export async function applyWidgetCodeOnWidgetNodes(
    creativeDataNode: ICreativeDataNode,
    elementsPool: WidgetElement[]
): Promise<void> {
    for (const element of elementsPool) {
        if (element.id) {
            const node = creativeDataNode.findNodeById_m(element.id);
            if (isWidgetNode(node)) {
                const { html, css, js } = await getWidgetCodeOfElement(element);
                node.html = html;
                node.css = css;
                node.js = js;
            }
        }
    }
}

export function mapElementToCustomWidgetProperties(
    element: IElement | IBrandLibraryElement | INewBrandLibraryElement
): IWidgetCustomProperty[] {
    return (element.properties as IWidgetElementProperty[])
        .filter(isCustomProperty)
        .map((property): IWidgetCustomProperty => {
            const value = deserializeWidgetPropertyValue(property);

            return {
                name: stripCustomPropertyPrefix(property.name),
                label: resolveNamespacedCharacters(property.label),
                unit: property.unit,
                value: value,
                versionPropertyId: property.versionPropertyId
            };
        });
}

export function isWidgetImage(value?: OneOfCustomPropertyValue): value is IWidgetImage {
    return (
        typeof value === 'object' &&
        'id' in value &&
        'src' in value &&
        typeof value.id === 'string' &&
        typeof value.src === 'string'
    );
}

export function isWidgetFont(value?: OneOfCustomPropertyValue): value is IFontStyle {
    return (
        typeof value === 'object' &&
        'id' in value &&
        'src' in value &&
        'style' in value &&
        typeof value.id === 'string' &&
        typeof value.src === 'string'
    );
}

export function getPartialImageAssetByProperty(
    property: IWidgetCustomProperty
): PartialLibraryAsset<ImageLibraryAsset> | undefined {
    if (!isWidgetImage(property.value)) {
        return;
    }

    const { src } = property.value || { src: '', id: '' };
    const name =
        src.split('/').find(x => x.match(/bmp|gif|jpeg|jpg|png|tiff|webp/)) ?? 'No image selected';
    return {
        name,
        url: src,
        thumbnail: { url: src, height: 0, width: 0 },
        original: { url: src, height: 0, width: 0 }
    };
}
